/**
 * StudySync Main App Component
 * Root component with routing, theming, and authentication
 */

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box } from "@mui/material";
import { Toaster } from "react-hot-toast";
import {
  SecureAuthProvider,
  useSecureAuth,
} from "./contexts/SecureAuthContext";
import { UserProvider, useUser } from "./contexts/UserContext";
import { theme } from "./theme";

// Import components (will be created next)
import LoadingSpinner from "./components/common/LoadingSpinner";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";

// Page components (will be created)
// LandingPage is the production landing (real RetroUI + Tailwind). It self-imports
// retroui.css + the self-hosted fonts, so it needs no theme wrapper.
import LandingPage from "./components/landing/LandingPage";
import ScheduleDemoPage from "./components/landing/ScheduleDemoPage";
import PeoplePage from "./components/landing/PeoplePage";
import ResearcherAuth from "./components/researcher/ResearcherAuth";
import ForgotPassword from "./components/researcher/ForgotPassword";
import ResearcherDashboard from "./components/researcher/ResearcherDashboard";
import StudyDashboard from "./components/researcher/StudyDashboard";
import AlertsTab from "./components/researcher/AlertsTab";
import ParticipantProfile from "./components/researcher/ParticipantProfile";
import ParticipantFlow from "./components/participant/ParticipantFlow";
import NewParticipantFlow from "./components/participant/NewParticipantFlow";
import ParticipantVerification from "./components/participant/ParticipantVerification";
import MultiStepVerification from "./components/participant/MultiStepVerification";
import ParticipantSuccess from "./components/participant/ParticipantSuccess";
import ParticipantInfoCollection from "./components/participant/ParticipantInfoCollection";
import ReconnectApp from "./components/participant/ReconnectApp";
import FitbitCallback from "./components/fitbit/FitbitCallback";
import FitbitReauthorization from "./components/fitbit/FitbitReauthorization";
import TemplateEditorPage from "./pages/TemplateEditorPage";
import PreferencesPage from "./pages/PreferencesPage";
import StudyAdherenceMonitorPage from "./components/researcher/adherence-monitor/StudyAdherenceMonitor";
import StudyVisualizationDashboard from "./components/researcher/visualization/StudyVisualizationDashboard";
import DataCoverageDashboard from "./components/researcher/data-coverage/data_coverage_dashboard";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AppEnrollmentRedirect from "./pages/AppEnrollmentRedirect";
import StudyMgmtDashboard from "./components/study-management/StudyMgmtDashboard";
import ConsentManager from "./components/consent_management/components/researcher/ConsentManager";
import ConsentFormCreatePage from "./components/consent_management/components/researcher/ConsentFormCreatePage";
import ParticipantConsentPage from "./components/consent_management/components/participant/ParticipantConsentPage";

// Master Account and Registration components
import { MasterDashboard, AdherenceMonitor } from "./components/admin";
import ResearcherRegistration from "./components/researcher/ResearcherRegistration";
import ApplicationStatus from "./components/researcher/ApplicationStatus";
import { CopilotDevHarness } from "./components/researcher/copilot/CopilotDevHarness";
import { CopilotDensityHarness } from "./components/researcher/copilot/CopilotDensityHarness";
import { CopilotTab } from "./components/researcher/copilot/CopilotTab";
import SmartRedirect from "./components/common/SmartRedirect";
import SupportWidget from "./components/support/SupportWidget";
import SpotlightProvider from "./components/spotlight/SpotlightProvider";

// DEV ONLY: expose a helper to dump the Firebase ID token in console
import { getAuth, onAuthStateChanged } from "firebase/auth";

// LOCAL-ONLY presentation scene (3 watch renderings + massive labels), lazy-loaded so
// three.js/r3f stays out of the main bundle. Not linked from any app UI.
const PresentationWatches = React.lazy(
  () => import("./pages/PresentationWatches"),
);

if (process.env.NODE_ENV === "development") {
  (window as any).dumpIdToken = async () => {
    const auth = getAuth();
    const user =
      auth.currentUser ||
      (await new Promise<any>((res) => onAuthStateChanged(auth, res)));
    if (!user) throw new Error("Not signed in");
    const token = await user.getIdToken(true);
    return token;
  };
}

// Protected Route Components
interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Legacy protected route - kept for potential future use
// const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
//   const { currentUser, loading } = useAuth();
//   if (loading) return <LoadingSpinner />;
//   if (!currentUser) return <Navigate to="/researcher/auth" replace />;
//   return <>{children}</>;
// };

// Master Account Protected Route
const MasterRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user: currentUser, isLoading: authLoading } = useSecureAuth();
  const { loading: userLoading, isMasterAccount } = useUser();

  if (authLoading || userLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    // Store the current URL with query params for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem("redirectAfterLogin", currentPath);
    return <Navigate to="/researcher/auth" replace />;
  }

  if (!isMasterAccount) {
    return <Navigate to="/researcher/dashboard" replace />;
  }

  return <>{children}</>;
};

// Researcher Protected Route
const ResearcherRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user: currentUser, isLoading: authLoading } = useSecureAuth();
  const { loading: userLoading, isApprovedResearcher, userProfile } = useUser();
  const location = useLocation();

  if (authLoading || userLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    // Store the current URL with query params for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem("redirectAfterLogin", currentPath);
    return <Navigate to="/researcher/auth" replace />;
  }

  // CRITICAL: Only approved researchers can access researcher routes
  if (!isApprovedResearcher) {
    // If user has noprofile status, redirect to registration
    if (userProfile?.status === "noprofile" || !userProfile) {
      // Allow them to stay on registration page, but redirect from everywhere else
      if (location.pathname !== "/researcher/register") {
        return <Navigate to="/researcher/register" replace />;
      }
      return <>{children}</>;
    }

    // If user has pending/rejected status, redirect to application-status
    if (
      userProfile &&
      (userProfile.status === "pending" || userProfile.status === "rejected")
    ) {
      // Allow them to stay on application-status page, but redirect from everywhere else
      if (location.pathname !== "/researcher/application-status") {
        return <Navigate to="/researcher/application-status" replace />;
      }
      return <>{children}</>;
    }

    // Default: redirect to registration if status is unknown
    return <Navigate to="/researcher/register" replace />;
  }

  // Approved researcher - allow access to all researcher routes
  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isLoading: authLoading } = useSecureAuth();
  const { loading: userLoading } = useUser();
  const location = useLocation();
  // Landing routes render full-bleed without the authenticated-app chrome
  // (header/footer) — the landing page brings its own navigation.
  // Local presentation scenes render full-bleed with NO app chrome (and no support FAB).
  const isPresentationRoute = location.pathname.startsWith("/present/");
  // Normalize a trailing slash so e.g. "/people/" is still recognized as a landing
  // route — otherwise the authenticated-app <Header/> renders ON TOP of the landing's
  // own nav (a duplicate nav bar) for any landing URL reached with a trailing slash.
  const landingPath = location.pathname.replace(/\/+$/, "") || "/";
  const isLandingRoute =
    isPresentationRoute ||
    landingPath === "/" ||
    landingPath === "/schedule-demo" ||
    landingPath === "/people" ||
    landingPath === "/terms-of-service" ||
    landingPath === "/privacy-policy";

  // Public landing routes bring their own nav and don't depend on auth/user state,
  // so they must render immediately — never block them on the auth spinner, or the
  // marketing page flashes "Loading..." until Firebase resolves (and hangs there if
  // auth can't initialize). Authenticated app routes still gate on loading below.
  if ((authLoading || userLoading) && !isLandingRoute) {
    return <LoadingSpinner />;
  }

  return (
    <SpotlightProvider>
      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        {!isLandingRoute && <Header />}

        <Box component="main" sx={{ flexGrow: 1, py: isLandingRoute ? 0 : 3 }}>
          <Routes>
            {/* Landing Page — the real RetroUI landing (self-imports retroui.css + fonts) */}
            <Route path="/" element={<LandingPage />} />

            {/* Schedule a Demo — standalone landing-styled page; its own custom URL */}
            <Route path="/schedule-demo" element={<ScheduleDemoPage />} />

            {/* People — standalone landing-styled team page; reached from nav/footer */}
            <Route path="/people" element={<PeoplePage />} />

            {/* Local DEV-ONLY presentation scene — 3 watch renderings + massive labels
                (~2:1). Guarded on NODE_ENV so it is DEAD-CODE-ELIMINATED from production
                builds: `process.env.NODE_ENV` is the string "production" there, so this
                becomes `{false && …}`, the <Route> is never registered, the lazy
                PresentationWatches chunk is never referenced, and /present/watches simply
                falls through to the catch-all redirect to "/". */}
            {process.env.NODE_ENV === "development" && (
              <Route
                path="/present/watches"
                element={
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <PresentationWatches />
                  </React.Suspense>
                }
              />
            )}

            {/* Local DEV-ONLY Copilot chart harness — review chart look-and-feel from fixtures
                (no auth, no DB). DEAD-CODE-ELIMINATED in production via the NODE_ENV guard. */}
            {process.env.NODE_ENV === "development" && (
              <>
                <Route path="/copilot-harness" element={<CopilotDevHarness />} />
                <Route path="/copilot-density" element={<CopilotDensityHarness />} />
              </>
            )}
            {process.env.NODE_ENV === "development" && (
              <Route
                path="/copilot-tab"
                element={
                  <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 4 }}>
                    <CopilotTab studyId="146" />
                  </Box>
                }
              />
            )}

            {/* Terms of Service */}
            <Route path="/terms-of-service" element={<TermsOfService />} />

            {/* Privacy Policy */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* Authentication */}
            <Route path="/researcher/auth" element={<ResearcherAuth />} />
            <Route
              path="/researcher/forgot-password"
              element={<ForgotPassword />}
            />

            {/* Smart Dashboard Redirect */}
            <Route path="/dashboard" element={<SmartRedirect />} />

            {/* Alerts Tab */}
            <Route
              path="/dashboard/alerts"
              element={
                <ResearcherRoute>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "100vh",
                    }}
                  >
                    <AlertsTab />
                  </Box>
                </ResearcherRoute>
              }
            />

            {/* Registration - Protected route for authenticated users only */}
            <Route
              path="/researcher/register"
              element={
                <ResearcherRoute>
                  <ResearcherRegistration />
                </ResearcherRoute>
              }
            />

            {/* Application Status - Protected route for authenticated users */}
            <Route
              path="/researcher/application-status"
              element={
                <ResearcherRoute>
                  <ApplicationStatus />
                </ResearcherRoute>
              }
            />

            {/* Master Account Routes */}
            <Route
              path="/master/dashboard"
              element={
                <MasterRoute>
                  <MasterDashboard />
                </MasterRoute>
              }
            />

            {/* Researcher Routes - Protected by approval status */}
            <Route
              path="/researcher/dashboard"
              element={
                <ResearcherRoute>
                  <ResearcherDashboard />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId"
              element={
                <ResearcherRoute>
                  <StudyDashboard />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/template-editor"
              element={
                <ResearcherRoute>
                  <TemplateEditorPage />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/preferences"
              element={
                <ResearcherRoute>
                  <PreferencesPage />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study-adherence-monitor"
              element={
                <ResearcherRoute>
                  <StudyAdherenceMonitorPage />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/visualizations"
              element={
                <ResearcherRoute>
                  <StudyVisualizationDashboard />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/data-coverage"
              element={
                <ResearcherRoute>
                  <DataCoverageDashboard />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/consent-management"
              element={
                <ResearcherRoute>
                  <ConsentManager />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/consent-management/create"
              element={
                <ResearcherRoute>
                  <ConsentFormCreatePage />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/participant-profile/:participantId/:studyId"
              element={
                <ResearcherRoute>
                  <ParticipantProfile />
                </ResearcherRoute>
              }
            />

            {/* Portfolio Overview - Master Only */}
            <Route
              path="/study-management/analytics"
              element={
                <MasterRoute>
                  <StudyMgmtDashboard />
                </MasterRoute>
              }
            />
            {/* Adherence Monitor - Admin Dashboard */}
            <Route
              path="/admin-adherence-monitor"
              element={
                <MasterRoute>
                  <AdherenceMonitor />
                </MasterRoute>
              }
            />

            {/* Participant Routes */}
            <Route path="/app/enroll" element={<AppEnrollmentRedirect />} />
            <Route
              path="/participant/enroll"
              element={<NewParticipantFlow />}
            />
            <Route path="/participant/consent/:token" element={<ParticipantConsentPage />} />
            <Route path="/participant/flow" element={<ParticipantFlow />} />
            <Route
              path="/participant/verify"
              element={<ParticipantVerification />}
            />
            <Route
              path="/participant/verify-email"
              element={<MultiStepVerification />}
            />
            <Route
              path="/participant/info"
              element={<ParticipantInfoCollection />}
            />
            <Route
              path="/participant/success"
              element={<ParticipantSuccess />}
            />
            <Route
              path="/participant/reconnect-app"
              element={<ReconnectApp />}
            />

            {/* Fitbit Integration Routes */}
            <Route path="/fitbit/callback" element={<FitbitCallback />} />
            <Route
              path="/fitbit/reauthorize"
              element={<FitbitReauthorization />}
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>

        {!isLandingRoute && <Footer />}
        {!isPresentationRoute && <SupportWidget />}
      </Box>
    </SpotlightProvider>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster position="top-right" toastOptions={{ duration: 5000 }} />
      <SecureAuthProvider>
        <UserProvider>
          <Router>
            <AppRoutes />
          </Router>
        </UserProvider>
      </SecureAuthProvider>
    </ThemeProvider>
  );
};

export default App;
